import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import './MiniCalendar.scss'

export interface MiniCalendarProps {
  selectedDate: Date | null
  /** Whether the calendar selection was made actively (shows check icon) */
  activeSelection: boolean
  /** Color variant for overdue (past) day styling */
  overdueVariant?: 'startdate' | 'deadline'
  onSelect: (date: Date) => void
  onDoubleClick?: (date: Date) => void
  /** Tage davor sind gesperrt (z. B. das Ende einer Wiederholung vor ihrem Start). */
  minDate?: Date
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date())
}

function isPast(date: Date): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d < today
}

/**
 * Build a calendar grid for a given month.
 * Returns 5-6 rows of 7 days each (Mon-Sun), including overflow days.
 */
function buildCalendarGrid(year: number, month: number): Date[][] {
  const firstDay = new Date(year, month, 1)
  // Monday = 0, Sunday = 6
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6

  const startDate = new Date(year, month, 1 - startDow)
  const rows: Date[][] = []

  for (let row = 0; row < 6; row++) {
    const week: Date[] = []
    for (let col = 0; col < 7; col++) {
      const d = new Date(startDate)
      d.setDate(startDate.getDate() + row * 7 + col)
      week.push(d)
    }
    // Skip row if entire week is in next month and we already have 5 rows
    if (rows.length >= 5 && week[0].getMonth() !== month) break
    rows.push(week)
  }

  return rows
}

const DAY_HEADERS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const DAY_HEADERS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export const MiniCalendar: React.FC<MiniCalendarProps> = ({
  selectedDate,
  activeSelection,
  overdueVariant = 'startdate',
  onSelect,
  onDoubleClick,
  minDate,
}) => {
  const { i18n } = useTranslation()
  const isDE = i18n.language.startsWith('de')

  const [viewMonth, setViewMonth] = useState(() => {
    const ref = selectedDate ?? new Date()
    return { year: ref.getFullYear(), month: ref.getMonth() }
  })

  // Skip the next auto-sync after an outside-month click — the user
  // explicitly stayed in the current view by clicking a leading/trailing
  // overflow day, and re-syncing would yank the calendar to the new
  // month against their intent. External changes (NL input) still pull
  // the view to the typed month.
  const skipNextSyncRef = useRef(false)

  // Navigate to the month of the selected date when it changes (e.g. via NL input)
  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false
      return
    }
    if (selectedDate) {
      const y = selectedDate.getFullYear()
      const m = selectedDate.getMonth()
      if (y !== viewMonth.year || m !== viewMonth.month) {
        setViewMonth({ year: y, month: m })
      }
    }
  }, [selectedDate]) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevMonth = useCallback(() => {
    setViewMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 }
      return { ...prev, month: prev.month - 1 }
    })
  }, [])

  const handleNextMonth = useCallback(() => {
    setViewMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 }
      return { ...prev, month: prev.month + 1 }
    })
  }, [])

  const grid = useMemo(() => buildCalendarGrid(viewMonth.year, viewMonth.month), [viewMonth.year, viewMonth.month])
  const monthLabel = new Date(viewMonth.year, viewMonth.month).toLocaleDateString(
    isDE ? 'de-DE' : 'en-US',
    { month: 'long', year: 'numeric' },
  )
  const dayHeaders = isDE ? DAY_HEADERS_DE : DAY_HEADERS_EN

  return (
    <div className={`mini-calendar${overdueVariant === 'deadline' ? ' mini-calendar--deadline' : ''}`}>
      {/* Month header with navigation */}
      <div className="mini-calendar__header">
        <span className="mini-calendar__month-label">{monthLabel}</span>
        <div className="mini-calendar__nav">
          <button
            type="button"
            className="mini-calendar__nav-btn"
            onClick={handlePrevMonth}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            className="mini-calendar__nav-btn"
            onClick={handleNextMonth}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Day-of-week headers */}
      <div className="mini-calendar__day-headers">
        {dayHeaders.map(dh => (
          <span key={dh} className="mini-calendar__day-header">{dh}</span>
        ))}
      </div>

      {/* Day grid */}
      <div className="mini-calendar__grid">
        {grid.map((week, wi) => (
          <div key={wi} className="mini-calendar__week">
            {week.map((date, di) => {
              const isCurrentMonth = date.getMonth() === viewMonth.month
              const isTodayDate = isToday(date)
              const isSelected = selectedDate ? isSameDay(date, selectedDate) : false
              const isActive = isSelected && activeSelection
              const isPassive = isSelected && !activeSelection
              const past = isPast(date) && !isTodayDate
              const disabled = minDate != null && date < new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())

              return (
                <button
                  key={di}
                  type="button"
                  disabled={disabled}
                  className={[
                    'mini-calendar__day',
                    !isCurrentMonth && 'mini-calendar__day--outside',
                    isTodayDate && 'mini-calendar__day--today',
                    isActive && 'mini-calendar__day--active',
                    isPassive && 'mini-calendar__day--passive',
                    past && 'mini-calendar__day--past',
                    disabled && 'mini-calendar__day--disabled',
                  ].filter(Boolean).join(' ')}
                  onClick={() => {
                    const d = new Date(date)
                    d.setHours(0, 0, 0, 0)
                    if (!isCurrentMonth) skipNextSyncRef.current = true
                    onSelect(d)
                  }}
                  onDoubleClick={() => {
                    if (onDoubleClick) {
                      const d = new Date(date)
                      d.setHours(0, 0, 0, 0)
                      if (!isCurrentMonth) skipNextSyncRef.current = true
                      onDoubleClick(d)
                    }
                  }}
                >
                  <span className="mini-calendar__day-number">{date.getDate()}</span>
                  {isActive && (
                    <span className="mini-calendar__day-check">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM11.71 6.71L7.71 10.71C7.51 10.9 7.26 11 7 11C6.74 11 6.49 10.9 6.29 10.71L4.29 8.71C3.9 8.32 3.9 7.68 4.29 7.29C4.68 6.9 5.32 6.9 5.71 7.29L7 8.59L10.29 5.29C10.68 4.9 11.32 4.9 11.71 5.29C12.1 5.68 12.1 6.32 11.71 6.71Z" fill="currentColor"/>
                      </svg>
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
